import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import ExtensionSection from "../components/ExtensionSection";
import {
  ArrowLeft,
  User,
  Lock,
  Smartphone,
  Monitor,
  ShieldCheck,
  Trash2,
  Copy,
  LogOut,
  KeyRound,
  Camera,
  Chrome,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountSettings = () => {
  const { user, refreshUser, logout, logoutAll } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("profile");

  return (
    <div
      className="min-h-screen bg-[#F9FAFB] dark:bg-[#09090B] text-[#0A0A0A] dark:text-[#FAFAFA]"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "calc(env(safe-area-inset-bottom) + 80px)",
      }}
    >
      {/* Sticky top bar with back+close */}
      <div
        className="sticky top-0 z-30 bg-white/95 dark:bg-[#18181B]/95 backdrop-blur border-b border-[#E4E4E7] dark:border-[#27272A]"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="flex items-center justify-between px-4 py-3 max-w-3xl mx-auto">
          <button
            data-testid="settings-back-button"
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-[#52525B] dark:text-[#A1A1AA] hover:text-[#0A0A0A] dark:hover:text-[#FAFAFA] active:scale-95 transition"
            aria-label="Geri"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Geri</span>
          </button>
          <h1 className="text-base sm:text-lg font-semibold">Hesap ve Güvenlik</h1>
          <button
            data-testid="settings-close-button"
            onClick={() => navigate("/dashboard")}
            className="text-[#52525B] dark:text-[#A1A1AA] hover:text-[#0A0A0A] dark:hover:text-[#FAFAFA] text-sm active:scale-95 transition"
            aria-label="Kapat"
          >
            ✕ Kapat
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-4">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-5 w-full bg-white dark:bg-[#18181B] border border-[#E4E4E7] dark:border-[#27272A]">
            <TabsTrigger value="profile" data-testid="settings-tab-profile" className="text-xs sm:text-sm">
              <User className="w-3.5 h-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">Profil</span>
            </TabsTrigger>
            <TabsTrigger value="password" data-testid="settings-tab-password" className="text-xs sm:text-sm">
              <Lock className="w-3.5 h-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">Şifre</span>
            </TabsTrigger>
            <TabsTrigger value="sessions" data-testid="settings-tab-sessions" className="text-xs sm:text-sm">
              <Monitor className="w-3.5 h-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">Oturumlar</span>
            </TabsTrigger>
            <TabsTrigger value="twofa" data-testid="settings-tab-2fa" className="text-xs sm:text-sm">
              <ShieldCheck className="w-3.5 h-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">2FA</span>
            </TabsTrigger>
            <TabsTrigger value="extension" data-testid="settings-tab-extension" className="text-xs sm:text-sm">
              <Chrome className="w-3.5 h-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">Eklenti</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-4">
            <ProfileSection user={user} refreshUser={refreshUser} />
          </TabsContent>

          <TabsContent value="password" className="mt-4">
            <PasswordSection />
          </TabsContent>

          <TabsContent value="sessions" className="mt-4">
            <SessionsSection logout={logout} logoutAll={logoutAll} />
          </TabsContent>

          <TabsContent value="twofa" className="mt-4">
            <TwoFASection refreshUser={refreshUser} />
          </TabsContent>

          <TabsContent value="extension" className="mt-4">
            <ExtensionSection />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

/* ============================================================ */
/* PROFILE                                                       */
/* ============================================================ */
const ProfileSection = ({ user, refreshUser }) => {
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const dirty = name.trim() !== (user?.name || "").trim();

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API_URL}/api/auth/profile`,
        { name },
        { withCredentials: true }
      );
      await refreshUser();
      toast.success("Profil güncellendi");
    } catch (e) {
      toast.error("Güncellenemedi");
    }
    setSaving(false);
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Avatar 5 MB'dan büyük olamaz");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API_URL}/api/auth/avatar`, fd, { withCredentials: true });
      await refreshUser();
      toast.success("Avatar güncellendi");
    } catch {
      toast.error("Avatar yüklenemedi");
    }
    setUploading(false);
  };

  return (
    <Card className="bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A]">
      <CardHeader>
        <CardTitle className="text-base">Profil Bilgileri</CardTitle>
        <CardDescription>Görünen ad ve profil fotoğrafı</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center gap-4">
          <div
            data-testid="profile-avatar-preview"
            className="w-20 h-20 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#D97706] flex items-center justify-center text-white text-2xl font-bold overflow-hidden border-2 border-[#F59E0B]/30"
          >
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="avatar" className="w-full h-full object-cover" />
            ) : (
              (user?.name || user?.email || "A").slice(0, 1).toUpperCase()
            )}
          </div>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => uploadAvatar(e.target.files?.[0])}
              data-testid="profile-avatar-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="profile-avatar-upload-button"
            >
              <Camera className="w-4 h-4 mr-2" />
              {uploading ? "Yükleniyor..." : "Fotoğraf Değiştir"}
            </Button>
            <p className="text-xs text-[#52525B] dark:text-[#A1A1AA] mt-1">JPG, PNG, WEBP – max 5 MB</p>
          </div>
        </div>

        <div>
          <Label className="text-[#52525B] dark:text-[#A1A1AA]">Yönetici E-posta</Label>
          <Input value={user?.email || ""} readOnly disabled className="mt-1 bg-[#F9FAFB] dark:bg-[#09090B]" />
          <p className="text-xs text-[#52525B] dark:text-[#A1A1AA] mt-1">
            E-posta değişikliği için backend/.env dosyasındaki ADMIN_EMAIL güncellenmelidir.
          </p>
        </div>

        <div>
          <Label htmlFor="display-name" className="text-[#52525B] dark:text-[#A1A1AA]">Görünen Ad</Label>
          <Input
            id="display-name"
            data-testid="profile-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
          />
        </div>

        <Button
          data-testid="profile-save-button"
          onClick={save}
          disabled={!dirty || saving}
          className="bg-[#F59E0B] hover:bg-[#D97706] text-white"
        >
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </Button>
      </CardContent>
      <AppearanceCard />
    </Card>
  );
};

/* ============================================================ */
/* APPEARANCE (Görünüm) — theme picker                            */
/* ============================================================ */
const AppearanceCard = () => {
  const { preference, resolved, setTheme } = useThemeSafe();
  const options = [
    { id: "system", label: "Sistem", desc: "Cihaz ayarını takip et", icon: MonitorIcon, testid: "appearance-system" },
    { id: "light",  label: "Açık",   desc: "Her zaman açık tema",    icon: SunIcon,     testid: "appearance-light" },
    { id: "dark",   label: "Koyu",   desc: "Her zaman koyu tema",    icon: MoonIcon,    testid: "appearance-dark" },
  ];
  return (
    <div className="mt-6 pt-6 border-t border-[#E4E4E7] dark:border-[#27272A] px-6 pb-6">
      <h3 className="text-sm font-semibold text-[#0A0A0A] dark:text-[#FAFAFA] mb-1">
        Görünüm
      </h3>
      <p className="text-xs text-[#52525B] dark:text-[#A1A1AA] mb-4">
        Tema tercihi hesabınıza kaydedilir; şu an <b>{resolved === "dark" ? "koyu" : "açık"}</b> tema aktif.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {options.map((o) => {
          const active = preference === o.id;
          const Icon = o.icon;
          return (
            <button
              key={o.id}
              data-testid={o.testid}
              onClick={() => setTheme(o.id)}
              className={
                "text-left rounded-lg border p-3 transition-colors flex items-start gap-3 " +
                (active
                  ? "border-[#F59E0B] bg-[#FEF3C7]/30 dark:bg-[#F59E0B]/10 ring-2 ring-[#F59E0B]/50"
                  : "border-[#E4E4E7] dark:border-[#27272A] bg-white dark:bg-[#09090B] hover:bg-[#F9FAFB] dark:hover:bg-[#18181B]")
              }
              aria-pressed={active}
            >
              <Icon
                className={
                  "w-5 h-5 mt-0.5 " +
                  (active
                    ? "text-[#F59E0B]"
                    : "text-[#52525B] dark:text-[#A1A1AA]")
                }
              />
              <div>
                <div className="text-sm font-semibold text-[#0A0A0A] dark:text-[#FAFAFA]">
                  {o.label}
                </div>
                <div className="text-xs text-[#52525B] dark:text-[#A1A1AA]">
                  {o.desc}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Lightweight inline icons — avoids adding new imports at file top.
const SunIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);
const MoonIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);
const MonitorIcon = (props) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><path d="M8 21h8M12 17v4" />
  </svg>
);
// Read theme via the ThemeContext hook.
import { useTheme as useAppTheme } from "../context/ThemeContext";
const useThemeSafe = () => useAppTheme();

/* ============================================================ */
/* PASSWORD                                                      */
/* ============================================================ */
const PasswordSection = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("Yeni şifre en az 8 karakter olmalı");
      return;
    }
    if (next !== confirm) {
      toast.error("Yeni şifre ile tekrarı eşleşmiyor");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/change-password`,
        { current_password: current, new_password: next },
        { withCredentials: true }
      );
      toast.success(
        `Şifre değiştirildi. ${data.revoked_other_sessions || 0} diğer oturum kapatıldı.`
      );
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Şifre değiştirilemedi");
    }
    setBusy(false);
  };

  return (
    <Card className="bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A]">
      <CardHeader>
        <CardTitle className="text-base">Şifreyi Değiştir</CardTitle>
        <CardDescription>
          Şifreniz değiştiğinde, mevcut cihaz dışındaki tüm oturumlar otomatik kapatılır.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4" data-testid="password-change-form">
          <div>
            <Label className="text-[#52525B] dark:text-[#A1A1AA]">Mevcut şifre</Label>
            <Input
              data-testid="password-current-input"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoComplete="current-password"
              className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
            />
          </div>
          <div>
            <Label className="text-[#52525B] dark:text-[#A1A1AA]">Yeni şifre (min 8)</Label>
            <Input
              data-testid="password-new-input"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              minLength={8}
              required
              autoComplete="new-password"
              className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
            />
          </div>
          <div>
            <Label className="text-[#52525B] dark:text-[#A1A1AA]">Yeni şifre (tekrar)</Label>
            <Input
              data-testid="password-confirm-input"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={8}
              required
              autoComplete="new-password"
              className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            data-testid="password-submit-button"
            className="bg-[#F59E0B] hover:bg-[#D97706] text-white"
          >
            {busy ? "Değiştiriliyor..." : "Şifreyi Değiştir"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

/* ============================================================ */
/* SESSIONS                                                      */
/* ============================================================ */
const SessionsSection = ({ logout, logoutAll }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmAll, setConfirmAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/sessions`, {
        withCredentials: true,
      });
      setSessions(data);
    } catch {
      toast.error("Oturumlar yüklenemedi");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async (jti) => {
    try {
      await axios.delete(`${API_URL}/api/auth/sessions/${jti}`, { withCredentials: true });
      toast.success("Oturum sonlandırıldı");
      load();
    } catch {
      toast.error("Sonlandırılamadı");
    }
  };

  const logoutOthers = async () => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/sessions/logout-others`,
        {},
        { withCredentials: true }
      );
      toast.success(`${data.revoked} oturum kapatıldı`);
      load();
    } catch {
      toast.error("İşlem başarısız");
    }
  };

  const doLogoutAll = async () => {
    setConfirmAll(false);
    await logoutAll();
  };

  const fmt = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("tr-TR"); } catch { return iso; }
  };
  const uaShort = (ua) => {
    if (!ua) return "Bilinmeyen cihaz";
    const isPhone = /iPhone|Android|Mobile/i.test(ua);
    const isMac = /Mac OS/i.test(ua);
    const isWin = /Windows/i.test(ua);
    const isLin = /Linux/i.test(ua);
    const browser = /Chrome/i.test(ua) ? "Chrome"
      : /Firefox/i.test(ua) ? "Firefox"
      : /Safari/i.test(ua) ? "Safari"
      : /Edg/i.test(ua) ? "Edge" : "Tarayıcı";
    const os = isPhone ? "Mobil" : isMac ? "macOS" : isWin ? "Windows" : isLin ? "Linux" : "Cihaz";
    return `${browser} • ${os}`;
  };

  return (
    <Card className="bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A]">
      <CardHeader>
        <CardTitle className="text-base">Aktif Oturumlar</CardTitle>
        <CardDescription>
          Aktif oturum sayısı: <strong>{sessions.length}</strong>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={logoutOthers}
            data-testid="sessions-logout-others-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Bu cihaz dışındakileri kapat
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirmAll(true)}
            data-testid="sessions-logout-all-button"
            className="border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Tüm cihazlardan çıkış
          </Button>
        </div>

        {loading && <p className="text-sm text-[#52525B]">Yükleniyor...</p>}

        <ul className="divide-y divide-[#E4E4E7] dark:divide-[#27272A] rounded border border-[#E4E4E7] dark:border-[#27272A]">
          {sessions.map((s) => (
            <li key={s.jti} className="flex items-center justify-between p-3 gap-3" data-testid="session-row">
              <div className="flex items-center gap-3 min-w-0">
                <Smartphone className="w-4 h-4 text-[#52525B] shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {uaShort(s.user_agent)}
                    {s.is_current && (
                      <span className="ml-2 text-[10px] uppercase font-bold text-[#F59E0B] bg-[#F59E0B]/10 px-1.5 py-0.5 rounded">
                        Bu cihaz
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#52525B] dark:text-[#A1A1AA]">
                    IP: {s.ip} • Son görülme: {fmt(s.last_seen)}
                  </div>
                </div>
              </div>
              {!s.is_current && (
                <button
                  onClick={() => revoke(s.jti)}
                  className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 p-2 rounded active:scale-95 transition"
                  aria-label="Oturumu kapat"
                  data-testid={`session-revoke-${s.jti.slice(0, 8)}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
          {!loading && sessions.length === 0 && (
            <li className="p-4 text-sm text-[#52525B] text-center">Aktif oturum yok</li>
          )}
        </ul>
      </CardContent>

      <AlertDialog open={confirmAll} onOpenChange={setConfirmAll}>
        <AlertDialogContent className="bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A]">
          <AlertDialogHeader>
            <AlertDialogTitle>Tüm cihazlardan çıkış yapılsın mı?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu cihaz dahil TÜM aktif oturumlar sonlandırılacak ve yeniden giriş yapmanız istenecek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={doLogoutAll}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="sessions-logout-all-confirm"
            >
              Evet, hepsinden çık
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

/* ============================================================ */
/* 2FA / TOTP                                                    */
/* ============================================================ */
const TwoFASection = ({ refreshUser }) => {
  const [status, setStatus] = useState({ enabled: false, recovery_codes_remaining: 0 });
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePwd, setDisablePwd] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/2fa/status`, { withCredentials: true });
      setStatus(data);
    } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const initSetup = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/2fa/init`, {}, { withCredentials: true });
      setSetupData(data);
    } catch {
      toast.error("QR üretilemedi");
    }
    setBusy(false);
  };

  const enable = async () => {
    if (!code || code.length < 6) { toast.error("6 haneli kod girin"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/2fa/enable`,
        { code },
        { withCredentials: true }
      );
      setRecoveryCodes(data.recovery_codes);
      setSetupData(null);
      setCode("");
      toast.success("2FA etkinleştirildi");
      await refreshUser();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Etkinleştirilemedi");
    }
    setBusy(false);
  };

  const disable = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/2fa/disable`,
        { current_password: disablePwd, code: disableCode },
        { withCredentials: true }
      );
      toast.success("2FA kapatıldı");
      setDisableOpen(false);
      setDisablePwd(""); setDisableCode("");
      await refreshUser();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kapatılamadı");
    }
    setBusy(false);
  };

  const regenerate = async () => {
    const c = window.prompt("Yeni kurtarma kodları üretmek için TOTP kodunuzu girin:");
    if (!c) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/2fa/recovery-codes/regenerate`,
        { code: c },
        { withCredentials: true }
      );
      setRecoveryCodes(data.recovery_codes);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Üretilemedi");
    }
    setBusy(false);
  };

  const copyCodes = () => {
    if (!recoveryCodes) return;
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    toast.success("Kurtarma kodları kopyalandı");
  };

  return (
    <Card className="bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A]">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#F59E0B]" />
          İki Aşamalı Doğrulama (TOTP)
        </CardTitle>
        <CardDescription>
          Google Authenticator, 1Password, Authy vb. uygulamalarla kullanılır. Opsiyoneldir.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && <p className="text-sm text-[#52525B]">Yükleniyor...</p>}

        {!loading && !status.enabled && !setupData && (
          <div>
            <p className="text-sm text-[#52525B] dark:text-[#A1A1AA] mb-3">
              2FA şu an <strong className="text-red-600 dark:text-red-400">KAPALI</strong>.
              Etkinleştirmek için önce QR üretin.
            </p>
            <Button
              onClick={initSetup}
              disabled={busy}
              data-testid="twofa-init-button"
              className="bg-[#F59E0B] hover:bg-[#D97706] text-white"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              QR Kodu Üret
            </Button>
          </div>
        )}

        {!loading && !status.enabled && setupData && (
          <div className="space-y-3">
            <div className="flex items-start gap-4 flex-col sm:flex-row">
              <img
                src={`data:image/png;base64,${setupData.qr_png_base64}`}
                alt="TOTP QR"
                className="w-48 h-48 border border-[#E4E4E7] dark:border-[#27272A] rounded bg-white"
                data-testid="twofa-qr-image"
              />
              <div className="flex-1 space-y-2">
                <p className="text-sm text-[#52525B] dark:text-[#A1A1AA]">
                  Authenticator uygulamanızla QR'ı tarayın, sonra 6 haneli kodu girin.
                </p>
                <div className="text-xs bg-[#F9FAFB] dark:bg-[#09090B] p-2 rounded border border-[#E4E4E7] dark:border-[#27272A] font-mono break-all">
                  {setupData.secret}
                </div>
                <Input
                  data-testid="twofa-verify-code-input"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="text-center text-lg tracking-widest bg-white dark:bg-[#09090B]"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={enable}
                    disabled={busy || code.length < 6}
                    data-testid="twofa-enable-button"
                    className="bg-[#F59E0B] hover:bg-[#D97706] text-white"
                  >
                    Etkinleştir
                  </Button>
                  <Button variant="outline" onClick={() => { setSetupData(null); setCode(""); }}>
                    Vazgeç
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && status.enabled && (
          <div className="space-y-3">
            <div className="text-sm">
              2FA <strong className="text-green-600 dark:text-green-400">AKTİF</strong>.
              Kalan kurtarma kodu: <strong>{status.recovery_codes_remaining}</strong>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={regenerate} data-testid="twofa-regen-codes-button">
                <KeyRound className="w-4 h-4 mr-2" />
                Kurtarma Kodlarını Yenile
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDisableOpen(true)}
                data-testid="twofa-disable-button"
                className="border-red-200 dark:border-red-800 text-red-600 dark:text-red-400"
              >
                2FA'yı Kapat
              </Button>
            </div>
          </div>
        )}

        {recoveryCodes && (
          <div className="mt-3 p-3 rounded border border-[#F59E0B]/30 bg-[#F59E0B]/5">
            <div className="text-sm font-medium mb-2 flex items-center justify-between">
              <span>Kurtarma Kodları (yalnızca bir kez gösterilir)</span>
              <button
                onClick={copyCodes}
                className="text-xs text-[#F59E0B] hover:underline flex items-center gap-1"
                data-testid="twofa-copy-codes"
              >
                <Copy className="w-3 h-3" />
                Kopyala
              </button>
            </div>
            <ul className="grid grid-cols-2 gap-1 text-xs font-mono">
              {recoveryCodes.map((c) => (
                <li key={c} className="bg-white dark:bg-[#09090B] px-2 py-1 rounded border border-[#E4E4E7] dark:border-[#27272A]">{c}</li>
              ))}
            </ul>
            <p className="text-xs text-[#52525B] dark:text-[#A1A1AA] mt-2">
              Bu kodları güvenli bir yerde saklayın. Telefonunuzu kaybederseniz kurtarma kodlarınızla giriş yapabilirsiniz.
            </p>
          </div>
        )}
      </CardContent>

      <AlertDialog open={disableOpen} onOpenChange={setDisableOpen}>
        <AlertDialogContent className="bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A]">
          <AlertDialogHeader>
            <AlertDialogTitle>2FA'yı kapat</AlertDialogTitle>
            <AlertDialogDescription>
              Mevcut şifreniz ve bir TOTP (veya kurtarma) kodu gerekir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <Input
              type="password"
              placeholder="Mevcut şifre"
              value={disablePwd}
              onChange={(e) => setDisablePwd(e.target.value)}
              data-testid="twofa-disable-password"
            />
            <Input
              placeholder="TOTP veya kurtarma kodu"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              data-testid="twofa-disable-code"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={disable}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="twofa-disable-confirm"
            >
              2FA'yı Kapat
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default AccountSettings;
